/**
 * LegalDocPage — Renderer generico per documenti legali Colucci.
 *
 * Usato via Router: <Route path="/legal/:slug" element={<LegalDocPage />} />
 * Carica il contenuto da `legalDocs.js`.
 *
 * Tutti i placeholder testuali del legale (es. "[DA INSERIRE]") sono renderizzati
 * con attributo `data-needs-review="true"` per audit automatico.
 */
import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { AlertTriangle, FileText, ArrowLeft, Shield } from "lucide-react";
import { LEGAL_DOCS } from "./legalDocs";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

function highlightPlaceholders(text) {
  // Rende visibili i placeholder [DA INSERIRE], [X — DA INSERIRE], ecc.
  const parts = text.split(/(\[[^\]]+\])/g);
  return parts.map((p, i) =>
    p.startsWith("[") && p.includes("DA INSERIRE") ? (
      <mark
        key={i}
        className="bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded text-xs font-mono border border-amber-300"
        data-needs-review="true"
      >
        {p}
      </mark>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

export default function LegalDocPage() {
  const { slug } = useParams();
  const doc = LEGAL_DOCS[slug];

  useEffect(() => {
    if (doc) document.title = `${doc.titolo} — Colucci Assicuratori`;
  }, [doc]);

  if (!doc) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-[#FAFAFA] pt-32 pb-20" data-testid="legal-not-found">
          <div className="max-w-3xl mx-auto px-6">
            <h1 className="text-3xl font-bold">Documento non trovato</h1>
            <p className="text-zinc-500 mt-2">Lo slug <code>{slug}</code> non corrisponde ad alcun documento legale.</p>
            <Link to="/legal" className="text-emerald-700 underline mt-4 inline-block">Torna all'indice legale</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-[#FAFAFA] pt-32 pb-20" data-testid={`legal-doc-${slug}`}>
        <div className="max-w-4xl mx-auto px-6">
          {/* Banner BOZZA */}
          {doc.bozza !== false && (
            <div className="mb-8 flex items-start gap-3 p-4 rounded-2xl bg-amber-50 border-2 border-amber-300" data-testid="legal-doc-bozza-banner" data-needs-review="true">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <strong>🚧 BOZZA — In attesa di validazione legale Colucci prima del go-live.</strong>
                <p className="mt-1 text-amber-800/85 text-xs">Questo documento è una bozza basata sui modelli IVASS standard 2026 (post Provv. 147/2024). Il consulente legale Colucci deve revisionare e completare i campi marcati come "DA INSERIRE" prima della pubblicazione operativa.</p>
              </div>
            </div>
          )}

          <Link to="/" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1 mb-6" data-testid="legal-back-home">
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </Link>

          <div className="bg-white rounded-3xl border border-zinc-200 p-8 sm:p-12 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-3">
              <FileText className="w-4 h-4" /> Documento legale
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-zinc-900 leading-tight mb-2" data-testid="legal-doc-title">
              {doc.titolo}
            </h1>
            {doc.sottotitolo && (
              <p className="text-sm text-zinc-500 mb-8">{doc.sottotitolo}</p>
            )}

            <div className="prose prose-sm max-w-none">
              {doc.sezioni.map((s, i) => (
                <section key={i} className="mb-8" data-testid={`legal-section-${i}`}>
                  <h2 className="font-heading text-lg font-semibold text-zinc-900 mb-3">{s.heading}</h2>
                  <p
                    className={`text-sm leading-relaxed whitespace-pre-line ${s.placeholder ? "text-zinc-700" : "text-zinc-600"}`}
                    data-needs-review={s.placeholder ? "true" : undefined}
                  >
                    {highlightPlaceholders(s.body)}
                  </p>
                </section>
              ))}
            </div>

            <div className="mt-12 pt-6 border-t border-zinc-100 text-xs text-zinc-400 flex items-center gap-2">
              <Shield className="w-3.5 h-3.5" />
              <span>Versione bozza v1.0-2026-02 · Colucci Assicuratori — RUI A000464752</span>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
